import { useQuery } from '@tanstack/react-query';

import { readUnpricedModels } from '@/lib/api/generated/api';
import type { UnpricedModelsView } from '@/lib/api/generated/model';
import { UsageError, isForbidden } from '@/features/workspace-usage';

import { llmProfileKeys } from '../queryKeys';

/**
 * How much of the current period ran on a model with no price, per model
 * (spec 092 follow-up) — the run-count/token size the old `UnpricedModels`
 * panel used to name on Overview, now surfaced beside the "No price" chip
 * itself instead of in a separate place.
 *
 * Current-period only, unlike the org-wide `useModelPrices` this sits next
 * to: "how much of *this period* is unpriced" is inherently a period
 * question, where "does this model have *a* price right now" is not.
 *
 * Unconditional, like `useModelPrices` — every model group needs to know
 * whether it has runs to show, not just the ones some other page already
 * flagged.
 */
export function useUnpricedModelRuns() {
  return useQuery<UnpricedModelsView>({
    queryKey: llmProfileKeys.unpricedModelRuns(),
    queryFn: async ({ signal }) => {
      const response = await readUnpricedModels({}, { signal });
      if (response.status === 200) return response.data;
      if (response.status === 403) {
        throw new UsageError(
          'forbidden',
          'Only an organization administrator can see which models are unpriced.',
        );
      }
      throw new UsageError(
        'unavailable',
        'Could not find out which models are unpriced.',
      );
    },
    refetchOnWindowFocus: false,
    retry: (failureCount, error) => !isForbidden(error) && failureCount < 2,
  });
}
